import { useUserContext } from "../../Hooks/useUserContext";
import { useUnitEquivalentService } from "../../Hooks/useUnitEquivalentService";
import { RouterLink } from "../../Shared/RouterLink";
import { AuthLoadingScreen } from "../../Shared/AuthLoadingScreen";
import { parseRelaxedJson } from "../../Helpers/jsonHelper";
import React, { useState, useEffect } from "react";
import {
	Grid,
	Typography,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Button,
	IconButton,
	Tooltip,
	Paper,
	TextField,
	Snackbar,
	SnackbarContent
} from "@material-ui/core";
import NotLoggedInIcon from "@material-ui/icons/MoodBad";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import ImportIcon from "@material-ui/icons/GetApp";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import ConfirmIcon from "@material-ui/icons/CheckCircle";
import green from "@material-ui/core/colors/green";
import { Spinner } from "../../Shared/Spinner";

export function UnitEquivalentAdmin(props) {
	const user = useUserContext(props.config);
	const service = useUnitEquivalentService(props.config);
	const [items, setItems] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [deletingId, setDeletingId] = useState(null);
	const [importText, setImportText] = useState("");
	const [showImport, setShowImport] = useState(false);
	const [toastOpen, setToastOpen] = useState(false);
	const [toastMessage, setToastMessage] = useState("");

	const normalizeAliasList = (value) => {
		if (!value) return [];
		const values = Array.isArray(value)
			? value
			: value.split(/\||\r?\n/);
		const parts = values.map((part) => (part ?? "").trim()).filter((part) => part);
		const seen = new Set();
		return parts.filter((part) => {
			const key = part.toLowerCase();
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	};

	const formatAliasesForOverview = (value) => normalizeAliasList(value).join("; ");

	const hasPermission = user?.isUnitAdmin ?? false;

	const sortedItems = [...items].sort((a, b) => {
		const aName = (a?.preferredUnit ?? "").toString().toLocaleLowerCase();
		const bName = (b?.preferredUnit ?? "").toString().toLocaleLowerCase();
		if (aName < bName) return -1;
		if (aName > bName) return 1;
		return 0;
	});

	useEffect(() => {
		document.title = "Unit Equivalents - " + props.config.appName;
		if (!hasPermission)
			return;

		loadItems();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	function loadItems() {
		setIsLoading(true);
		service.getAll(
			(response) => {
				if (response && response.status === 200 && response.data) {
					setItems(response.data);
				}
				setIsLoading(false);
			},
			(error) => {
				console.error(error);
				alert("Failed to load unit equivalents");
				setIsLoading(false);
			}
		);
	}

	function handleDelete(event, item) {
		if (event && event.preventDefault)
			event.preventDefault();

		if (!window.confirm(`Delete "${item.preferredUnit}"?`))
			return;

		setDeletingId(item.unitEquivalentId);
		service.deleteItem(item.unitEquivalentId, () => {
			loadItems();
			setDeletingId(null);
		}, (error) => {
			console.error(error);
			alert("Failed to delete unit equivalent");
			setDeletingId(null);
		});
	}

	function handleImport() {
		try {
			const parsed = parseRelaxedJson(importText);
			const unitEquivalents = parsed.map(item => {
				if (Array.isArray(item)) {
					return {
						preferredUnit: item[0],
						Aliases: (item || []).slice(1),
						forceDecimalPlacesRounding: null,
						notes: ""
					};
				} else {
					return {
						preferredUnit: item.preferredUnit,
						Aliases: Array.isArray(item.aliases) ? item.aliases : normalizeAliasList(item.aliases),
						forceDecimalPlacesRounding: item.forceDecimalPlacesRounding ?? null,
						notes: item.notes || ""
					};
				}
			});

			service.importItems(unitEquivalents, (response) => {
				if (response && response.status === 200) {
					setItems(response.data);
					setShowImport(false);
					setImportText("");
					setToastMessage("Import successful!");
					setToastOpen(true);
				} else {
					alert("Failed to import");
				}
			}, (error) => {
				console.error(error);
				alert("Failed to import: " + (error.response?.data || error.message));
			});
		} catch (e) {
			alert("Invalid JSON format. Expected array of objects with preferredUnit, aliases, optional forceDecimalPlacesRounding and notes, or legacy array format.");
		}
	}

	function handleExport() {
		const exportData = items.map(item => {
			const aliasList = normalizeAliasList(item.aliases ?? item.Aliases);
			return {
				preferredUnit: item.preferredUnit,
				aliases: aliasList,
				forceDecimalPlacesRounding: item.forceDecimalPlacesRounding ?? null,
				notes: item.notes ?? ""
			};
		});

		const jsonString = JSON.stringify(exportData, null, 2);
		const blob = new Blob([jsonString], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
		link.download = `unit-equivalents-${timestamp}.json`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		setTimeout(() => URL.revokeObjectURL(url), 100);
		setToastMessage("Export successful!");
		setToastOpen(true);
	}

	if (!user.isInitialized) {
		return <AuthLoadingScreen />;
	}

	if (!hasPermission)
		return (
			<Grid container spacing={8}>
				<Grid item xs={12}>
					<NotLoggedInIcon fontSize="large" />
					<Typography variant="subtitle1">
						Sorry, you must {user.isLoggedIn ? "" : "be logged in and "}have unit admin permissions to view this page!
					</Typography>
					<Typography style={{ paddingLeft: 40 }} variant="subtitle1">
						Logged in: {user.isLoggedIn ? "True" : "False"}
					</Typography>
					<Typography style={{ paddingTop: 20 }} variant="subtitle1">
						<RouterLink to="/login" >
							{user.isLoggedIn ? "" : "Login Now"}
						</RouterLink>
					</Typography>
				</Grid>
			</Grid>
		);

	return (
		<Grid container spacing={3} style={{ padding: 20 }}>
			<Grid item xs={12}>
				<Typography variant="h4" gutterBottom>
					Unit Equivalents
				</Typography>
			</Grid>
			<Grid item xs={12} style={{ marginBottom: 12 }}>
				<Button
					variant="contained"
					color="primary"
					component={RouterLink}
					to="/admin/unit-equivalents/create"
				>
					<AddIcon style={{ marginRight: 10 }} />
					Add Unit Equivalent
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={() => setShowImport(!showImport)}
					style={{ marginLeft: 12 }}
				>
					<ImportIcon style={{ marginRight: 8 }} />
					Import
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={handleExport}
					style={{ marginLeft: 12 }}
				>
					<SaveAltIcon style={{ marginRight: 8 }} />
					Export
				</Button>
			</Grid>
			{showImport && (
				<Grid item xs={12}>
					<Paper style={{ padding: 20, marginBottom: 20 }}>
						<Typography variant="h6" gutterBottom>
							Import from Config Format
						</Typography>
						<Typography variant="body2" gutterBottom>
							Paste the UnitEquivalents JSON array from your config file:
						</Typography>
						<TextField
							fullWidth
							multiline
							rows={8}
							variant="outlined"
							value={importText}
							onChange={(e) => setImportText(e.target.value)}
							placeholder={'[\n  [ "mg", "milligram", "milligrams" ],\n  [ "g", "gm", "grams", "gram" ],\n  ...\n]'}
							style={{ marginBottom: 10 }}
						/>
						<Button
							variant="contained"
							color="primary"
							onClick={handleImport}
							style={{ marginRight: 10 }}
						>
							Import
						</Button>
						<Button
							variant="outlined"
							onClick={() => { setShowImport(false); setImportText(""); }}
						>
							Cancel
						</Button>
					</Paper>
				</Grid>
			)}
			<Grid item xs={13}>{/* add +1 to this xs value to fix a display issue */}
				<Paper>
					{isLoading ? (
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
							<Spinner size={24} style={{ marginRight: 10 }} />
							<Typography>Loading...</Typography>
						</div>
					) : (
						<Table className="nowrap-table condensed-table">
							<TableHead>
								<TableRow>
								<TableCell align="center"></TableCell>
								<TableCell>Preferred Unit</TableCell>
									<TableCell>Aliases</TableCell>
									<TableCell align="center">Decimal Places</TableCell>
									<TableCell>Notes</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{sortedItems.map((item) => (
									<TableRow key={item.unitEquivalentId}>
										<TableCell align="center">
											<Tooltip title="Edit">
												<IconButton
													color="primary"
													size="small"
													className="rb-icon-btn"
													component={RouterLink}
													to={`/admin/unit-equivalents/${item.unitEquivalentId}/edit`}
													aria-label="Edit unit equivalent"
												>
													<EditIcon fontSize="small" />
												</IconButton>
											</Tooltip>
											<Tooltip title="Delete">
												<IconButton
													color="primary"
												size="small"
													className="rb-icon-btn"
													onClick={(e) => handleDelete(e, item)}
													disabled={deletingId === item.unitEquivalentId}
													aria-label="Delete unit equivalent"
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</TableCell>
									<TableCell>{item.preferredUnit}</TableCell>
									<TableCell>{formatAliasesForOverview(item.aliases)}</TableCell>
									<TableCell align="center">{item.forceDecimalPlacesRounding === null || item.forceDecimalPlacesRounding === undefined ? "allow fractions" : item.forceDecimalPlacesRounding}</TableCell>
									<TableCell>{item.notes}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</Paper>
			</Grid>
			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={toastOpen}
				autoHideDuration={3000}
				onClose={() => setToastOpen(false)}
				style={{ marginTop: 20 }}
			>
				<SnackbarContent
					style={{ backgroundColor: green[600] }}
					message={
						<div style={{ display: "flex", alignItems: "center" }}>
							<ConfirmIcon />
							<span style={{ paddingLeft: 10 }}>{toastMessage}</span>
						</div>
					}
				/>
			</Snackbar>
		</Grid>
	);
}

